import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Download, Music } from 'lucide-react';
import SEO from '../../components/SEO';
import SeoNav from '../../components/SeoNav';

const schema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What should a musician invoice include?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'A musician invoice should include your name or band name and contact details, the client or venue name, a unique invoice number and performance date, payment due date, a description of the services (performance type, duration, number of musicians), performance fee, any travel and accommodation expenses, equipment hire or backline costs if applicable, and your payment details.',
      },
    },
    {
      '@type': 'Question',
      name: 'How do musicians invoice for live performances?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'For live performances, list the performance as a single line item with the agreed fee. Add separate line items for any extras — travel expenses, overnight accommodation, sound engineer hire, or equipment rental. If a deposit was paid at booking, show it as a deduction so the client can clearly see the balance due. Reference the booking agreement date and venue on the invoice.',
      },
    },
    {
      '@type': 'Question',
      name: 'Should musicians charge VAT on performance fees?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Only if VAT-registered. In the UK, live music performance is standard-rated at 20% VAT once turnover exceeds £90,000. In Australia, musical performances are subject to 10% GST if turnover exceeds AUD $75,000. In India, entertainment and performance services attract 18% GST above the ₹20 lakh threshold. Many independent musicians earn below these thresholds and do not need to charge tax.',
      },
    },
    {
      '@type': 'Question',
      name: 'Can musicians invoice for studio session work?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Session musicians should invoice for studio time separately from live performance fees. Typical line items for session work include: session fee (per track or per day), overdub sessions, travel to studio, and any buyout fee for waiving ongoing royalty rights. If you retain any rights to the recording, note this clearly on the invoice or in the accompanying agreement.',
      },
    },
    {
      '@type': 'Question',
      name: 'Is this musician invoice template free?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Invilic provides a free musician invoice template. Fill in your performance or session details online and download a professional PDF instantly. Free for up to 5 invoices per month with no sign-up required.',
      },
    },
  ],
};

export default function MusicianInvoiceTemplatePage() {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <SEO
        title="Musician Invoice Template UK: Free PDF for Musicians & Performers | Invilic"
        description="Free musician invoice template for UK and international musicians. Bill for live performances, session work, travel. HMRC self-assessment ready. No sign-up needed."
        canonical="https://www.invilic.com/musician-invoice-template"
        schema={schema}
      />

      <SeoNav />

      <main className="max-w-4xl mx-auto px-6 py-16">
        <div className="text-center mb-16">
          <span className="inline-block bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wider mb-4">
            Free Template
          </span>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 dark:text-white leading-tight mb-5">
            Musician Invoice Template
          </h1>
          <p className="text-xl text-gray-500 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed mb-8">
            A professional invoice template for musicians, bands, and session artists. Bill for performances, studio sessions, and travel. Download a clean PDF in seconds — no account needed.
          </p>
          <Link
            to="/create/freelancer"
            className="inline-flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white font-semibold px-8 py-4 rounded-xl shadow-lg shadow-purple-600/20 transition-all duration-200 text-lg"
          >
            Use Free Template <ArrowRight className="w-5 h-5" />
          </Link>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-3">Free · No sign-up · PDF in seconds</p>
        </div>

        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
            Built for Every Type of Music Professional
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { title: 'Live performers & bands', desc: 'Bill venues, event organisers, and corporate clients for live performances and touring work.' },
              { title: 'Session musicians', desc: 'Invoice recording studios, producers, and artists for studio sessions and recording days.' },
              { title: 'Music tutors & teachers', desc: 'Bill students and schools for individual lessons, group classes, and curriculum work.' },
              { title: 'DJs & producers', desc: 'Issue professional invoices for DJ sets, music production, and beat licensing fees.' },
            ].map(({ title, desc }) => (
              <div key={title} className="flex gap-4 p-5 rounded-xl border border-gray-100 dark:border-gray-800">
                <div className="w-10 h-10 rounded-xl bg-purple-100 dark:bg-purple-900/40 flex items-center justify-center flex-shrink-0">
                  <Music className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <div className="font-semibold text-gray-900 dark:text-white text-sm mb-1">{title}</div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
            What's in the Musician Invoice Template
          </h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              'Artist / band name and contact details',
              'Client / venue name and address',
              'Invoice number and performance date',
              'Payment due date',
              'Performance or session description',
              'Performance fee or day rate',
              'Travel and accommodation expenses',
              'Equipment or backline hire',
              'Deposit paid — balance remaining',
              'Subtotal, tax and discount',
              'Total amount due',
              'Bank details or payment link',
            ].map(f => (
              <div key={f} className="flex items-center gap-3 p-4 rounded-xl border border-purple-100 dark:border-purple-800/30 bg-purple-50 dark:bg-purple-900/10">
                <CheckCircle className="w-4 h-4 text-purple-600 dark:text-purple-400 flex-shrink-0" />
                <span className="text-sm text-gray-700 dark:text-gray-300">{f}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
            How to Create Your Music Invoice
          </h2>
          <div className="grid sm:grid-cols-3 gap-6">
            {[
              { n: '1', title: 'Open the template', desc: 'Click "Use Free Template" above. No software to install, no account needed.' },
              { n: '2', title: 'Add your performance details', desc: 'Enter your fee, expenses, deposit paid, and any extra charges.' },
              { n: '3', title: 'Download PDF', desc: 'Download a professional PDF invoice ready to send to the venue or client.' },
            ].map(({ n, title, desc }) => (
              <div key={n} className="text-center p-6 rounded-2xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50">
                <div className="w-10 h-10 rounded-full bg-purple-600 text-white font-bold text-lg flex items-center justify-center mx-auto mb-4">
                  {n}
                </div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">{title}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">{desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">
            Musician Invoice Template: FAQs
          </h2>
          <div className="space-y-4">
            {schema.mainEntity.map(({ name, acceptedAnswer }) => (
              <details key={name} className="group border border-gray-100 dark:border-gray-800 rounded-xl overflow-hidden">
                <summary className="flex items-center justify-between px-6 py-4 cursor-pointer font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-900/50 list-none">
                  {name}
                  <ArrowRight className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-90 flex-shrink-0 ml-4" />
                </summary>
                <p className="px-6 pb-5 text-gray-500 dark:text-gray-400 text-sm leading-relaxed">
                  {acceptedAnswer.text}
                </p>
              </details>
            ))}
          </div>
        </section>

        {/* UK HMRC section */}
        <section className="mb-16 p-6 rounded-2xl border border-purple-100 dark:border-purple-800/40 bg-purple-50 dark:bg-purple-900/20">
          <h2 className="font-bold text-gray-900 dark:text-white mb-3">Musician Invoicing in the UK — HMRC Rules</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            UK-based musicians registered as sole traders must complete a Self Assessment tax return each year. Key points:
          </p>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              { label: '£1,000 trading allowance', detail: 'If your music income is below £1,000/year, you may not need to file or pay tax — the trading allowance covers it.' },
              { label: 'Self Assessment threshold: £1,000+', detail: 'Earn more than £1,000 from music? Register for Self Assessment with HMRC by 5 October following the tax year.' },
              { label: '20% VAT threshold: £90,000', detail: 'Live music is standard-rated at 20% VAT. You only register once your annual taxable turnover exceeds £90,000.' },
              { label: 'Musician invoice template UK', detail: 'Your invoice must show your name, a unique invoice number, description of the performance, your fee, and payment terms.' },
            ].map(({ label, detail }) => (
              <div key={label} className="p-3 bg-white dark:bg-gray-900/50 rounded-xl border border-purple-100 dark:border-purple-800/30">
                <p className="font-semibold text-gray-900 dark:text-white text-xs mb-1">{label}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">{detail}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="text-center py-12 border-t border-gray-100 dark:border-gray-800">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            Create Your Music Invoice Now for Free
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mb-8">Professional PDF. No account required. Ready in 2 minutes.</p>
          <Link
            to="/create/freelancer"
            className="inline-flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white font-semibold px-8 py-4 rounded-xl shadow-lg shadow-purple-600/20 transition-all duration-200 text-lg"
          >
            <Download className="w-5 h-5" /> Get Free Musician Invoice Template
          </Link>
        </section>
      </main>
    </div>
  );
}
